<template>
  <article class="resource" ref="resource">
    <div class="columns is-multiline">
      <div class="column is-8 is-9-desktop">
        <div class="resource__headers">
          <h2 class="title is-2 resource__title">{{ websiteTitle }}</h2>
          <h3 class="title is-3 resource__organisation" v-if="organisation">{{ organisation }}</h3>
          <p class="is-small resource__location" v-if="location">{{ location }}</p>
        </div>
        <div class="resource__star-rating">
          <template v-for="i in 5">
            <star-svg :key=i :className="starIcon(i)"></star-svg>
          </template>
          <a v-if="ratingLink" :href="ratingLink" class="resource__rating-link">What's this?</a>
        </div>
        <div
          class="resource__summary rich-text"
          :class="{ 'hidden': collapsed }"
          v-html="summary"
        ></div>
        <p class="resource__last-reviewed" :class="{ 'hidden': collapsed }">
          Last reviewed: {{ lastReviewed }}
        </p>
        <a :href="websiteUrl" class="resource__link" :class="{ 'hidden': collapsed }">
          <span class="icon icon--external"><external-link-svg></external-link-svg></span>
          <span>Visit Website</span>
        </a>
      </div>
      <div class="column is-4 is-3-desktop">
        <ul v-if="badges.length > 0" class="resource__badges" :class="{ 'hidden': collapsed }">
          <li v-for="badge in badges" :key="badge" class="resource__badge">
            <span
              v-if="badgeIcons[badge]"
              class="resource__badge_icon icon"
              :class="badgeIcons[badge]"
            ></span>
            {{ badge }}
          </li>
        </ul>
      </div>
      <div class="column">
        <div
          v-if="tags.length > 0"
          class="is-small resource__tags"
          :class="{ 'hidden': collapsed }"
        >
          <span>Tags: </span>
          <template v-for="(tag, index) in tags">
            <button
              class="resource__tag"
              :aria-label="ariaLabel(tag)"
              :key="tag"
              @click.prevent="updateSearch(tag)"
            >
              <span>{{ tag }}</span>
            </button>
            <template v-if="index !== tags.length - 1">, </template>
          </template>
        </div>
        <div class="resource__button-container is-hidden-tablet">
          <button
            class="resource__size_toggle"
            :class="{ 'is-active': !collapsed }"
            @click="toggleResourceSize"
          >
            <span>{{ buttonText }}</span>
            <arrow-svg></arrow-svg>
          </button>
        </div>
      </div>
    </div>
  </article>
</template>

<script>
import ArrowSvg from '../svg/arrow.vue';
import ExternalLinkSvg from '../svg/external-link.vue';
import StarSvg from '../svg/star.vue';

export default {
  name: 'resource',
  components: {
    ArrowSvg,
    ExternalLinkSvg,
    StarSvg,
  },
  props: {
    badges: Array,
    lastReviewed: String,
    location: String,
    organisation: String,
    ratingLink: String,
    score: Number,
    summary: String,
    tags: Array,
    websiteTitle: String,
    websiteUrl: String,
  },
  data() {
    return {
      collapsed: true,
    };
  },
  computed: {
    searchTerm: {
      get() {
        return this.$store.state.searchTerm;
      },
      set(value) {
        this.$store.commit('setSearchTerm', value);
      },
    },
    buttonText() {
      return this.collapsed ? 'Read more' : 'Close';
    },
    badgeIcons() {
      return {
        'Academic Institution': 'icon--academic',
        'Alcohol Industry': 'icon--alcohol',
        'Community-led Source': 'icon--community',
        'Government Affiliated': 'icon--government',
        'Mobile Friendly': 'icon--mobile',
        'Paywall or Login Barrier': 'icon--paywall',
        'Pharmaceuticals Industry': 'icon--pharmaceutical',
        'Tobacco Industry': 'icon--tobacco',
      };
    },
  },
  methods: {
    /**
     * return the class required for the star rating in the position
     * @param { Int } pos
     * @returns { String } either 'star--full', 'star--half' or 'star--empty'
     */
    starIcon(pos) {
      if (this.score >= pos) {
        return 'star--full';
      }
      if (this.score === pos - 0.5) {
        return 'star--half';
      }
      return 'star--empty';
    },
    /**
     * Scroll to the top of an article
     */
    scrollToArticleTop() {
      // End constant is accounting for Navbar size
      const top = this.$refs.resource.getBoundingClientRect().top + window.pageYOffset - 105;
      window.scrollTo(0, top);
    },
    /**
     * Toggle the height of a resource article
     */
    toggleResourceSize() {
      this.collapsed = !this.collapsed;
      this.scrollToArticleTop();
    },
    /**
     * Update searchTerm with tag and Emit search
     */
    updateSearch(tag) {
      window.scrollTo(0, 0);
      this.searchTerm = tag;
      this.$emit('search');
    },
    /**
     * Return text for aria-label attribute
     */
    ariaLabel(tag) {
      return `filter results by ${tag}`;
    },
  },
};
</script>
